const DEFAULT_OMIT_FIELDS = [
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'createdDate',
  'updatedDate',
  'createdOn',
  'updatedOn',
];

const DEFAULT_RENAME_EXCEPTIONS = {
  id: 'ID',
  projectId: 'ProjectID',
  url: 'URL',
  apiUrl: 'ApiURL',
};

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === '[object Object]';

const toCamelKey = (key) => {
  const normalized = String(key)
    .replace(/[_\-\s]+(.)?/g, (_, chr) => (chr ? chr.toUpperCase() : ''))
    .replace(/^[A-Z]/, (chr) => chr.toLowerCase());

  return normalized;
};

const toPascalKey = (key) => {
  const camel = toCamelKey(key);
  return camel.charAt(0).toUpperCase() + camel.slice(1);
};

const convertKeysDeep = (value, convertKey, options = {}) => {
  if (Array.isArray(value)) {
    return value.map((entry) => convertKeysDeep(entry, convertKey, options));
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const {
    renameMap = {},
    omitFields = [],
    nullHandling = 'keep',
    keepOriginalKeys = false,
    includeMappedKeys = true,
  } = options;

  return Object.entries(value).reduce((accumulator, [rawKey, rawVal]) => {
    const camelRawKey = toCamelKey(rawKey);
    const normalizedValue = convertKeysDeep(rawVal, convertKey, options);

    if (nullHandling === 'drop' && normalizedValue === null) {
      return accumulator;
    }

    if (omitFields.includes(camelRawKey)) {
      return accumulator;
    }

    const mappedKey = renameMap[camelRawKey] ?? convertKey(camelRawKey);

    if (keepOriginalKeys && !(rawKey in accumulator)) {
      accumulator[rawKey] = normalizedValue;
    }

    if (includeMappedKeys && !(mappedKey in accumulator)) {
      accumulator[mappedKey] = normalizedValue;
    }

    return accumulator;
  }, {});
};

const valuesAreEquivalent = (left, right) => {
  if (left === right) {
    return true;
  }

  if (left === null || left === undefined || right === null || right === undefined) {
    return left === right;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((entry, index) => valuesAreEquivalent(entry, right[index]));
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    return leftKeys.every((key) => Object.prototype.hasOwnProperty.call(right, key) && valuesAreEquivalent(left[key], right[key]));
  }

  return false;
};

const dedupeCaseVariantKeysDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map(dedupeCaseVariantKeysDeep);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const dedupedEntries = Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    const normalizedValue = dedupeCaseVariantKeysDeep(entryValue);
    const uppercaseFirst = key.charAt(0).toUpperCase() + key.slice(1);

    const isLowercaseFirst = key.charAt(0) === key.charAt(0).toLowerCase();
    const hasUppercaseVariant = Object.prototype.hasOwnProperty.call(value, uppercaseFirst);

    if (
      isLowercaseFirst &&
      uppercaseFirst !== key &&
      hasUppercaseVariant &&
      valuesAreEquivalent(value[uppercaseFirst], entryValue)
    ) {
      return accumulator;
    }

    accumulator[key] = normalizedValue;
    return accumulator;
  }, {});

  return dedupedEntries;
};

export const normalizeResponseData = (payload, config = {}) => {
  if (payload == null) {
    return payload;
  }

  const {
    envelope = ['data', 'result', 'items'],
    renameMap = {},
    keepOriginalKeys = true,
    includeMappedKeys = true,
    dedupeCaseVariantKeys = false,
  } = config;

  const envelopeCandidates = Array.isArray(envelope) ? envelope : [envelope];

  const unwrapped = (() => {
    if (!isPlainObject(payload)) {
      return payload;
    }

    const match = envelopeCandidates.find((key) => key in payload);
    return match ? payload[match] : payload;
  })();

  const converted = convertKeysDeep(unwrapped, toCamelKey, {
    renameMap,
    nullHandling: 'keep',
    keepOriginalKeys,
    includeMappedKeys,
  });

  if (!dedupeCaseVariantKeys) {
    return converted;
  }

  return dedupeCaseVariantKeysDeep(converted);
};

export const normalizeRequestData = ({
  data,
  method,
  contract = {},
}) => {
  if (data == null) {
    return data;
  }

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return data;
  }

  if (!isPlainObject(data) && !Array.isArray(data)) {
    return data;
  }

  const upperMethod = String(method || 'GET').toUpperCase();

  const defaultMethodCase = {
    POST: 'camel',
    PATCH: 'pascal',
  };

  const requestCase = contract.requestCase || defaultMethodCase[upperMethod] || null;

  const omitFields = [
    ...DEFAULT_OMIT_FIELDS,
    ...(Array.isArray(contract.omitFields) ? contract.omitFields : []),
  ];

  const renameMap = {
    ...DEFAULT_RENAME_EXCEPTIONS,
    ...(contract.renameMap || {}),
  };

  const nullHandling = contract.nullHandling || 'keep';

  const converted = requestCase === 'pascal'
    ? convertKeysDeep(data, toPascalKey, { omitFields, renameMap, nullHandling })
    : requestCase === 'camel'
      ? convertKeysDeep(data, toCamelKey, { omitFields, renameMap, nullHandling })
      : data;

  if (contract.envelopeKey) {
    return { [contract.envelopeKey]: converted };
  }

  return converted;
};

export const normalizeApiError = ({ error, url, method, correlationId }) => {
  const status = error?.response?.status ?? null;
  const responseData = error?.response?.data ?? null;
  const message =
    responseData?.message ||
    responseData?.error ||
    error?.message ||
    'Unknown API error';

  const isCanceled =
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError' ||
    error?.code === 'ERR_CANCELED';

  const isTimeout =
    error?.code === 'ECONNABORTED' ||
    String(error?.message || '').toLowerCase().includes('timeout');

  const isNetworkError = !status && !isCanceled;

  return {
    status,
    message,
    details: responseData,
    isNetworkError,
    isTimeout,
    isCanceled,
    endpoint: url,
    method: String(method || 'GET').toUpperCase(),
    correlationId,
  };
};

export const shouldRetryRequest = ({ method, status, error, attempt, maxAttempts }) => {
  if (attempt >= maxAttempts) {
    return false;
  }

  const upperMethod = String(method || 'GET').toUpperCase();
  const isIdempotent = ['GET', 'HEAD', 'OPTIONS'].includes(upperMethod);

  if (!isIdempotent) {
    return false;
  }

  const transientStatuses = [408, 425, 429, 500, 502, 503, 504];
  const hasTransientStatus = transientStatuses.includes(status || 0);

  const isNetworkError = !status;
  const isCanceled = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';

  return hasTransientStatus || (isNetworkError && !isCanceled);
};

export const getRetryDelayMs = (attempt) => {
  const base = 300;
  const exp = base * Math.pow(2, Math.max(0, attempt - 1));
  const jitter = Math.floor(Math.random() * 250);
  return exp + jitter;
};

export const createCorrelationId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `cid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};
